import { GetProp, Image, message, UploadProps } from 'antd';
import { useState } from 'react';
import {
  ImageWrapper,
  Mask,
  MaskButton,
  Placeholder,
  StyledUpload,
} from './styles';
import {
  DeleteOutlined,
  EyeOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { UploadBusinessType, UploadFileType } from '../../types';
import { useTranslation } from 'react-i18next';

type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0];

const getBase64 = (img: FileType, callback: (url: string) => void) => {
  const reader = new FileReader();
  reader.addEventListener('load', () => callback(reader.result as string));
  reader.readAsDataURL(img);
};

const beforeUpload = (file: FileType, error: (key: string) => void) => {
  const isValidType =
    file.type === 'image/jpeg' ||
    file.type === 'image/png' ||
    file.type === 'image/bmp';
  if (!isValidType) {
    error('upload_image_type_error');
  }
  const isLt2M = file.size / 1024 / 1024 < 2;
  if (!isLt2M) {
    error('upload_image_size_error');
  }
  return isValidType && isLt2M;
};

const ImageUpload = ({
  fixedWidth = true,
  imageUrl,
  onUploaded,
  onLoadingChange,
  businessType,
}: {
  fixedWidth?: boolean;
  imageUrl?: string;
  onUploaded?: (response?: any) => void;
  onLoadingChange?: (isLoading: boolean) => void;
  businessType?: UploadBusinessType;
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const handleChange: UploadProps['onChange'] = (info) => {
    if (info.file.status === 'uploading') {
      setLoading(true);
      onLoadingChange?.(true);
      return;
    }
    if (info.file.status === 'done') {
      getBase64(info.file.originFileObj as FileType, (url) => {
        setLoading(false);
        onLoadingChange?.(false);
        onUploaded?.(info.file.response);
      });
    }
  };

  const handlePreview = (e: React.MouseEvent) => {
    e.stopPropagation();
    setPreviewOpen(true);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    onUploaded?.();
  };

  return (
    <>
      <StyledUpload
        name="files"
        data={{
          businessType,
          fileType: UploadFileType.IMAGE,
        }}
        headers={{
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        }}
        listType="picture-card"
        className={`avatar-uploader ${fixedWidth && 'fixed-width'}`}
        showUploadList={false}
        action={`${process.env.REACT_APP_SERVICE_URL}/file`}
        beforeUpload={(file) =>
          beforeUpload(file, (key) => message.error(t(key)))
        }
        onChange={handleChange}
      >
        {imageUrl ? (
          <ImageWrapper>
            <img src={imageUrl} alt="avatar" />
            <Mask className="mask" onClick={(e) => e.stopPropagation()}>
              <MaskButton
                icon={<EyeOutlined />}
                onClick={handlePreview}
                title={t('preview')}
              />
              <MaskButton
                icon={<DeleteOutlined />}
                onClick={handleDelete}
                title={t('delete')}
              />
            </Mask>
          </ImageWrapper>
        ) : (
          <button
            style={{ border: 0, background: 'none', cursor: 'pointer' }}
            type="button"
          >
            {loading ? (
              <LoadingOutlined />
            ) : (
              <Placeholder style={{ marginTop: 8 }}>
                {t('click_to_upload_image')}
              </Placeholder>
            )}
          </button>
        )}
      </StyledUpload>
      <Image
        preview={{
          visible: previewOpen,
          onVisibleChange: (visible) => setPreviewOpen(visible),
        }}
        src={imageUrl}
        style={{ display: 'none' }}
      />
    </>
  );
};

export default ImageUpload;
